import React, { useEffect, useMemo } from 'react';
import { Box, Divider, Grid, Paper, Typography } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useFormik } from 'formik';
import { AppDispatch, RootStateType } from '@Store';
import SBDropdown from '@Components/Dropdown';
import SbButton from '@Components/Button';
import { downloadCarrierLabel } from '@Reducers/ShipmentHistory/actions';
import { setIsWarehouseSelectedINFedX } from '@Reducers/Warehouse';
import { vendorAccountType } from '@Reducers/AdminShipment/Type';

import {
  onForward,
  DutyTypeOptions,
  PaymentTypeOptions,
  linexInformationValidation,
  DeclareCurrencyTypeOptions,
} from './constants';
import SbTextField from '@Components/Textfield';

interface Props {
  onClickForward: () => void;
  onClose?: () => void;
  initialStates: any;
  isEditable?: boolean;
  showFedExDetails: boolean;
  btnText: string;
}

const LinexInformation: React.FC<Props> = ({
  onClickForward,
  onClose,
  initialStates,
  isEditable = false,
  showFedExDetails,
  btnText,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { isWarehouseSelectedINFedX } = useSelector((state: RootStateType) => state.Warehouse);
  const { shipmentById, vendersAccount, linexDetails } = useSelector((state: RootStateType) => state.adminShipment);

  const linexSettings = useSelector((state: RootStateType) => state.user.carrierSetting.linex);

  // Country options
  const countryOptions = [
    { value: 'UK', display: 'UK' },
    { value: 'EUROPE', display: 'Europe' },
  ];

  // Service options based on country
  const getServiceOptions = (country: string) => {
    if (country === 'UK') {
      return [{ value: 'HOME', display: 'HOME' }];
    } else if (country === 'EUROPE') {
      return [
        { value: 'HOME', display: 'HOME' },
        { value: 'HOME_YCC', display: 'HOME_YCC' },
      ];
    }
    return [];
  };

  const accountOptions = useMemo(
    () =>
      vendersAccount?.map((item: vendorAccountType) => ({
        value: `${item.accountNo}-${item._id}`,
        display: item.name,
      })) ?? [],
    [vendersAccount],
  );

  // Custom validation based on new fields
  const validateForm = (values: any) => {
    const errors: any = {};

    // Existing validations
    if (!values.accountNo) errors.accountNo = 'Required';
    if (!values.dutyType) errors.dutyType = 'Required';
    if (!values.PaymentType) errors.PaymentType = 'Required';
    if (!values.declareCurrency) errors.declareCurrency = 'Required';

    // New validations
    if (!values.country) errors.country = 'Required';
    if (!values.serviceCode) errors.serviceCode = 'Required';

    // IOSS number validation for Europe -> HOME
    if (values.country === 'EUROPE' && values.serviceCode === 'HOME' && !values.ShipperVATNumber) {
      errors.ShipperVATNumber = 'IOSS Number is required for Europe HOME service';
    }

    return errors;
  };

  const formik = useFormik({
    enableReinitialize: true,
    validateOnChange: false,
    initialValues: {
      accountNo: '',
      dutyType: '',
      PaymentType: '',
      declareCurrency: '',
      country: 'UK',
      serviceCode: '',
      ShipperVATNumber: '',
      ...initialStates,
    },
    validationSchema: linexInformationValidation,
    validate: validateForm,
    onSubmit: values => {
      if (!isWarehouseSelectedINFedX || isWarehouseSelectedINFedX === 'false') {
        dispatch(setIsWarehouseSelectedINFedX('false'));
      }

      // Prepare data for backend
      const submitValues = {
        ...values,
        // If HOME_YCC is selected for Europe or UK is selected, send empty string for ShipperVATNumber
        ShipperVATNumber:
          (values.country === 'EUROPE' && values.serviceCode === 'HOME_YCC') || values.country === 'UK'
            ? ''
            : values.ShipperVATNumber,
      };

      onForward({
        values: submitValues,
        shipmentNumber: shipmentById?.shipmentNumber || '',
        dispatch,
        nextFunc: onClickForward,
      });
    },
  });

  const { values, setFieldValue, errors, handleSubmit, handleChange } = formik;

  // Get service options based on selected country
  const serviceOptions = getServiceOptions(values.country);

  // Effect to handle service when country changes - ONLY when country changes from user interaction
  useEffect(() => {
    // Skip if we have initialStates with values (editing mode)
    if (initialStates?.country || initialStates?.serviceCode) {
      return;
    }

    // Only run for new entries (no initialStates)
    if (values.country === 'UK') {
      setFieldValue('serviceCode', 'HOME');
      setFieldValue('ShipperVATNumber', '');
    }
    // Reset service when switching to Europe
    if (values.country === 'EUROPE' && values.serviceCode === 'HOME') {
      // Keep HOME if it was already selected
    } else if (values.country === 'EUROPE') {
      setFieldValue('serviceCode', '');
    }
  }, [values.country, initialStates]);

  // Effect to handle ShipperVATNumber when service changes - ONLY when service changes from user interaction
  useEffect(() => {
    // Skip if we have initialStates with values (editing mode)
    if (initialStates?.country || initialStates?.serviceCode) {
      return;
    }

    // Only run for new entries (no initialStates)
    if (values.country === 'EUROPE' && values.serviceCode === 'HOME_YCC') {
      setFieldValue('ShipperVATNumber', '');
    }
    if (values.country === 'UK') {
      setFieldValue('ShipperVATNumber', '');
    }
  }, [values.serviceCode, values.country, initialStates]);

  // Handle ShipperVATNumber from initialStates (note the capital 'S')
  useEffect(() => {
    if (initialStates?.ShipperVATNumber !== undefined) {
      setFieldValue('ShipperVATNumber', initialStates.ShipperVATNumber);
    }
  }, [initialStates]);

  useEffect(() => {
    if (values.accountNo?.length && accountOptions?.length) {
      const matched = accountOptions.find(opt => opt.value.startsWith(values.accountNo));

      if (matched) {
        setFieldValue('accountNo', matched.value);
      }
    }
  }, [values.accountNo, accountOptions]);

  if (!showFedExDetails) return null;

  return (
    <Paper variant="outlined" sx={{ my: '2rem', p: '2rem' }}>
      <Typography variant="h6" gutterBottom>
        Linex Information
      </Typography>

      <Grid container spacing={4}>
        {/* Country Selection */}
        <Grid item xs={12} md={6}>
          <SBDropdown
            disabled={!isEditable}
            id="country"
            label="Country"
            required
            placeholder="Select Country"
            name="country"
            value={values.country ? [values.country] : []}
            options={countryOptions}
            onChange={value => {
              setFieldValue('country', value[0]);
            }}
            error={errors.country as string}
          />
        </Grid>

        {/* Service Dropdown - Now using serviceCode */}
        <Grid item xs={12} md={6}>
          <SBDropdown
            disabled={!isEditable || !values.country}
            id="serviceCode"
            label="Service"
            required
            placeholder="Select Service"
            name="serviceCode"
            value={values.serviceCode ? [values.serviceCode] : []}
            options={serviceOptions}
            onChange={value => {
              setFieldValue('serviceCode', value[0]);
            }}
            error={errors.serviceCode as string}
          />
        </Grid>

        {/* IOSS Number (ShipperVATNumber) */}
        {values.country === 'EUROPE' && values.serviceCode === 'HOME' && (
          <Grid item xs={12} md={6}>
            <SbTextField
              disabled={!isEditable}
              label={`IOSS Number`}
              required
              value={values.ShipperVATNumber}
              placeholder="Enter IOSS Number"
              onChange={handleChange}
              name="ShipperVATNumber"
              error={errors.ShipperVATNumber as string}
            />
          </Grid>
        )}

        {/* Duty Type with Notice */}
        <Grid item xs={12} md={6}>
          <Box>
            <SBDropdown
              disabled={!isEditable}
              id="dutyType"
              label="Duty Type"
              required
              placeholder="Select"
              name="dutyType"
              value={values.dutyType ? [values.dutyType] : []}
              options={DutyTypeOptions}
              onChange={val => setFieldValue('dutyType', val[0] || '')}
              error={errors.dutyType as string}
            />

            <Typography variant="caption" color="warning.main" sx={{ mt: 0.5, ml: 1.5, display: 'block' }}>
              Note: If shipment amount is less than 150 euro then choose DDP option
            </Typography>
          </Box>
        </Grid>

        <Grid item xs={12} md={6}>
          <SBDropdown
            disabled={!isEditable}
            id="accountNo"
            label="Linex Account"
            required
            placeholder="Select"
            name="accountNo"
            value={values.accountNo ? [values.accountNo] : []}
            options={linexSettings?.accountNo?.length ? linexSettings?.accountNo : accountOptions}
            onChange={value => {
              setFieldValue('accountNo', value[0]);
            }}
            error={errors.accountNo as string}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <SBDropdown
            disabled={!isEditable}
            id="PaymentType"
            label="Payment Type"
            required
            placeholder="Select"
            name="PaymentType"
            value={values.PaymentType ? [values.PaymentType] : []}
            options={PaymentTypeOptions}
            onChange={val => setFieldValue('PaymentType', val[0] || '')}
            error={errors.PaymentType as string}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <SBDropdown
            disabled={!isEditable}
            id="declareCurrency"
            label="Declare Currency"
            required
            placeholder="Select"
            name="declareCurrency"
            value={values.declareCurrency ? [values.declareCurrency] : []}
            options={DeclareCurrencyTypeOptions}
            onChange={val => setFieldValue('declareCurrency', val[0] || '')}
            error={errors.declareCurrency as string}
          />
        </Grid>
      </Grid>

      <Divider sx={{ my: 3 }} />

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={3}>
          <SbButton
            disabled={!isEditable}
            variant="contained"
            sx={{ borderRadius: 1 }}
            fullWidth
            onClick={() => handleSubmit()}
          >
            {btnText}
          </SbButton>
        </Grid>

        {onClose && (
          <Grid item xs={12} sm={6} md={3}>
            <SbButton variant="outlined" sx={{ borderRadius: 1 }} fullWidth onClick={onClose}>
              Cancel
            </SbButton>
          </Grid>
        )}
      </Grid>

      {linexDetails?.response && (
        <Grid container mt={3}>
          <Grid item xs="auto" border="1px solid" className="border-grey font-medium md-text p-2" borderRadius={1}>
            This shipment has been forwarded.
            <Box
              component="span"
              sx={{
                color: '#485fc7',
                textDecoration: 'underline',
                cursor: 'pointer',
                ml: 1,
              }}
              onClick={() => {
                if (shipmentById?.shipmentNumber) {
                  dispatch(downloadCarrierLabel(shipmentById.shipmentNumber));
                }
              }}
            >
              Download the carrier AWB
            </Box>
          </Grid>
        </Grid>
      )}
    </Paper>
  );
};

export default LinexInformation;
